import React from 'react'
import type { Organization, OrganizationBranding } from '../../../types/access'
import { brandingBanner, brandingField, brandingLogo, brandingName, brandingOrgId } from './adminPanelUtils'

export type OrganizationBrandingDto = {
  orgId?: string | null
  org_id?: string | null
  name?: string | null
  shortName?: string | null
  short_name?: string | null
  logoUrl?: string | null
  logo_url?: string | null
  bannerUrl?: string | null
  banner_url?: string | null
  primaryColor?: string | null
  primary_color?: string | null
  accentColor?: string | null
  accent_color?: string | null
  metadata?: Record<string, unknown> | null
}

export type BrandLike = OrganizationBranding | OrganizationBrandingDto | null | undefined

type BrandSource = {
  branding?: BrandLike
  organization?: Organization | null
  fallbackName?: string | null
}

const logoUrl = (branding?: BrandLike) => {
  if (!branding) return ''
  return brandingLogo(branding as OrganizationBranding)
    || String((branding as OrganizationBrandingDto).logoUrl || (branding as OrganizationBrandingDto).logo_url || '')
}

const bannerUrl = (branding?: BrandLike) => {
  if (!branding) return ''
  return brandingBanner(branding as OrganizationBranding)
    || brandingField(branding as OrganizationBranding, 'bannerUrl', 'banner_url')
    || String((branding as OrganizationBrandingDto).bannerUrl || (branding as OrganizationBrandingDto).banner_url || '')
}

const displayName = (source: BrandSource) => {
  const branding = source.branding
  const dtoName = branding ? String((branding as OrganizationBrandingDto).name || (branding as OrganizationBrandingDto).shortName || (branding as OrganizationBrandingDto).short_name || '') : ''
  return dtoName || brandingName(branding as OrganizationBranding | undefined, source.organization?.name || source.fallbackName || 'Organization')
}

const brandColor = (branding?: BrandLike) => {
  if (!branding) return undefined
  return brandingField(branding as OrganizationBranding, 'primaryColor', 'primary_color')
    || brandingField(branding as OrganizationBranding, 'accentColor', 'accent_color')
    || undefined
}

const initials = (name: string) => {
  const parts = name.trim().split(/\s+/).filter(Boolean)
  return (parts.length > 1 ? `${parts[0][0]}${parts[1][0]}` : name.slice(0, 2)).toUpperCase()
}

export const findBrand = (
  orgId: string | null | undefined,
  organizations: Organization[],
  brandingRows: OrganizationBranding[] = [],
): BrandSource => {
  const organization = organizations.find((org) => org.id === orgId) || null
  return {
    organization,
    branding: brandingRows.find((row) => brandingOrgId(row) === orgId) || null,
    fallbackName: organization?.name || null,
  }
}

export const OrganizationAvatar: React.FC<BrandSource & { size?: 'sm' | 'md' }> = ({
  branding,
  organization,
  fallbackName,
  size = 'md',
}) => {
  const name = displayName({ branding, organization, fallbackName })
  const logo = logoUrl(branding)
  const color = brandColor(branding)
  if (logo) return <img src={logo} alt={`${name} logo`} className={`admin-brand-logo admin-brand-logo--${size}`} />
  return (
    <div className={`admin-brand-logo admin-brand-logo--fallback admin-brand-logo--${size}`} style={color ? { background: color } : undefined} aria-label={name} title={name}>
      {initials(name)}
    </div>
  )
}

export const BrandBanner: React.FC<BrandSource & { compact?: boolean }> = ({
  branding,
  organization,
  fallbackName,
  compact = false,
}) => {
  const name = displayName({ branding, organization, fallbackName })
  const banner = bannerUrl(branding)
  const color = brandColor(branding)
  if (banner) {
    return (
      <img
        src={banner}
        alt={`${name} banner`}
        className={`admin-brand-banner ${compact ? 'admin-brand-banner--compact' : ''}`}
        style={{ width: '100%', maxHeight: compact ? 96 : 180, objectFit: 'cover', borderRadius: 12 }}
      />
    )
  }
  return (
    <div
      className={`admin-brand-banner admin-brand-banner--fallback ${compact ? 'admin-brand-banner--compact' : ''}`}
      style={{ background: color || 'var(--surface-muted)', borderRadius: 12, minHeight: compact ? 72 : 120 }}
      aria-label={`${name} banner placeholder`}
      title={name}
    />
  )
}

export const BrandBadge: React.FC<BrandSource & { label?: string; emphasized?: boolean }> = ({
  branding,
  organization,
  fallbackName,
  label,
  emphasized = false,
}) => {
  const name = displayName({ branding, organization, fallbackName })
  return (
    <div className={`admin-brand-badge ${emphasized ? 'admin-brand-badge--emphasis' : ''}`}>
      <OrganizationAvatar branding={branding} organization={organization} fallbackName={fallbackName} size={emphasized ? 'md' : 'sm'} />
      <span>
        {label ? <small>{label}</small> : null}
        <strong>{name}</strong>
      </span>
    </div>
  )
}

export const BrandPair: React.FC<{
  primary: BrandSource
  secondary?: BrandSource
  primaryLabel?: string
  secondaryLabel?: string
}> = ({ primary, secondary, primaryLabel = 'For', secondaryLabel = 'Posted by' }) => (
  <div className="admin-brand-pair">
    <BrandBadge {...primary} label={primaryLabel} emphasized />
    {secondary ? <BrandBadge {...secondary} label={secondaryLabel} /> : null}
  </div>
)
